import React, { useState, useEffect } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Grid, Icon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import { get, postNoResponse, getFile } from "utils/WebService";
import moment from "moment";
import { toCurrency } from "utils/NumberUtils";
import { infoAviso } from "utils/Functions";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";

export default function Acordos({ recordId, idDevedorCarteira, isViewOnly, changeTab, isHonorariosTab = false, onDataUpdated, refreshKey, preloadedAcordos, isFilho = false }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [acordos, setAcordos] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [sbMessage, setSbMessage] = useState("");
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    
    const { userPerfils } = useUser();
    const isMembro = userPerfils?.some(p => (p.nome && p.nome.toLowerCase().includes("membro")) || (p.ccNome && p.ccNome.toLowerCase().includes("membro")));

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedAcordoToCancel, setSelectedAcordoToCancel] = useState(null);

    const [baixaDialogOpen, setBaixaDialogOpen] = useState(false);
    const [selectedParcela, setSelectedParcela] = useState(null);
    const [baixaValor, setBaixaValor] = useState("");
    const [baixaData, setBaixaData] = useState("");
    const [baixaDesconto, setBaixaDesconto] = useState("");
    const [baixaAcrescimo, setBaixaAcrescimo] = useState("");

    const [updateBoletoDialogOpen, setUpdateBoletoDialogOpen] = useState(false);
    const [selectedBoletoParcela, setSelectedBoletoParcela] = useState(null);
    const [novaDataBoleto, setNovaDataBoleto] = useState("");
    const [simulacaoBoleto, setSimulacaoBoleto] = useState(null);
    const [simulacaoLoading, setSimulacaoLoading] = useState(false);

    const [editParcelaDialogOpen, setEditParcelaDialogOpen] = useState(false);
    const [selectedParcelaEdit, setSelectedParcelaEdit] = useState(null);
    const [novoVencimentoParcela, setNovoVencimentoParcela] = useState("");

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    const fetchAcordos = () => {
        if (!recordId) return;

        if (preloadedAcordos !== undefined) {
            let resAcordos = preloadedAcordos || [];
            if (idDevedorCarteira) {
                resAcordos = resAcordos.filter(a => a.idDevedorCarteira === idDevedorCarteira);
            }
            if (isHonorariosTab) {
                resAcordos = resAcordos.filter(a => a.isHonorario === true || a.honorario === true);
            } else {
                if (!isFilho) {
                    resAcordos = resAcordos.filter(a => a.isHonorario !== true && a.honorario !== true);
                }
            }
            setAcordos(resAcordos);
            return;
        }

        setLoading(true);
        get(`/acordo/list/${recordId}`)
            .then(res => {
                if (res && Array.isArray(res)) {
                    let resAcordos = res;
                    if (idDevedorCarteira) {
                        resAcordos = resAcordos.filter(a => a.idDevedorCarteira === idDevedorCarteira);
                    }
                    if (isHonorariosTab) {
                        resAcordos = resAcordos.filter(a => a.isHonorario === true || a.honorario === true);
                    } else {
                        if (!isFilho) {
                            resAcordos = resAcordos.filter(a => a.isHonorario !== true && a.honorario !== true);
                        }
                    }
                    setAcordos(resAcordos);
                }
            })
            .catch(err => console.error("Erro ao carregar acordos:", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchAcordos();
    }, [recordId, refreshKey, idDevedorCarteira, preloadedAcordos, isFilho]);

    const getSituacaoLabel = (sit) => {
        switch (sit) {
            case 1: return <MDTypography variant="caption" color="info" fontWeight="medium">Ativo</MDTypography>;
            case 2: return <MDTypography variant="caption" color="error" fontWeight="medium">Quebrado</MDTypography>;
            case 3: return <MDTypography variant="caption" color="success" fontWeight="medium">Quitado</MDTypography>;
            case 4: return <MDTypography variant="caption" color="secondary" fontWeight="medium">Cancelado</MDTypography>;
            default: return <MDTypography variant="caption" color="text">Desconhecido</MDTypography>;
        }
    };

    const isParcelaAtrasada = (p) => {
        if (p.situacao === 2 || p.situacao === 4) return false;
        if (p.situacao === 3) return true;
        if (p.vencimento && moment(p.vencimento).isBefore(moment(), 'day') && (!p.valorPago || p.valorPago <= 0)) {
            return true;
        }
        return false;
    };

    const getParcelaSituacaoLabel = (p) => {
        if (p.situacao === 2) return "Pago";
        if (p.situacao === 4) return "Cancelado";
        if (isParcelaAtrasada(p)) return "Atrasado";
        if (p.situacao === 1 && p.valorPago > 0) return "Parcial";
        return "Aberto";
    };

    const getBoletoSituacaoBadge = (sit, isAtrasada) => {
        if (!sit) return null;
        if (sit === 7 && isAtrasada) return null;
        switch (sit) {
            case 3: return <MDTypography variant="caption" color="info" sx={{display:'block', fontSize: '0.65rem', fontWeight: 'bold'}}>Boleto Registrado</MDTypography>;
            case 4: return <MDTypography variant="caption" color="error" sx={{display:'block', fontSize: '0.65rem', fontWeight: 'bold'}}>Boleto Rejeitado</MDTypography>;
            case 5: return <MDTypography variant="caption" color="success" sx={{display:'block', fontSize: '0.65rem', fontWeight: 'bold'}}>Boleto Pago</MDTypography>;
            case 7: return <MDTypography variant="caption" color="secondary" sx={{display:'block', fontSize: '0.65rem', fontWeight: 'bold'}}>Boleto Baixado</MDTypography>;
            default: return null;
        }
    };

    const handleOpenCancelDialog = (idAcordo) => {
        setSelectedAcordoToCancel(idAcordo);
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
        setSelectedAcordoToCancel(null);
    };

    const handleConfirmCancelAcordo = () => {
        setCancelDialogOpen(false);
        if (!selectedAcordoToCancel) return;
        
        setLoading(true);
        postNoResponse("/acordo/cancel", { acordoId: selectedAcordoToCancel })
            .then(() => {
                showSnackbar("Acordo cancelado com sucesso!", true);
                fetchAcordos();
            })
            .catch((err) => {
                showSnackbar(err.message || "Erro ao cancelar acordo.", false);
                setLoading(false);
            })
            .finally(() => {
                setSelectedAcordoToCancel(null);
            });
    };

    const handleOpenBaixaDialog = (parcela) => {
        setSelectedParcela(parcela);
        let valorRestante = parcela.valorParcela;
        if (parcela.valorPago) {
            valorRestante = valorRestante - parcela.valorPago;
        }
        setBaixaValor(valorRestante > 0 ? valorRestante.toFixed(2) : parcela.valorParcela);
        setBaixaData(moment().format("YYYY-MM-DD"));
        setBaixaDialogOpen(true);
    };

    const handleCloseBaixaDialog = () => {
        setBaixaDialogOpen(false);
        setSelectedParcela(null);
        setBaixaValor("");
        setBaixaData("");
        setBaixaDesconto("");
        setBaixaAcrescimo("");
    };

    const handleConfirmBaixa = () => {
        if (!selectedParcela || !baixaValor || !baixaData) return;
        setLoading(true);
        postNoResponse("/acordo/parcela/baixar", {
            idParcela: selectedParcela.id,
            valorPago: parseFloat(baixaValor.toString().replace(",", ".")),
            dataPagamento: baixaData,
            formaPagamento: "Manual",
            valorDesconto: baixaDesconto ? parseFloat(baixaDesconto.toString().replace(",", ".")) : 0,
            valorAcrescimo: baixaAcrescimo ? parseFloat(baixaAcrescimo.toString().replace(",", ".")) : 0
        })
        .then(() => {
            showSnackbar("Parcela baixada com sucesso!", true);
            handleCloseBaixaDialog();
            fetchAcordos();
        })
        .catch((err) => {
            showSnackbar(err.message || "Erro ao baixar parcela.", false);
            setLoading(false);
        });
    };

    const handleEstornar = (parcela) => {
        infoAviso(
            "Deseja realmente estornar a baixa desta parcela? Esta ação reabrirá o saldo e registrará a ocorrência no histórico.",
            () => {
                setLoading(true);
                postNoResponse("/acordo/parcela/estornar", { id: parcela.id, isContrato: false })
                    .then(() => {
                        showSnackbar("Baixa estornada com sucesso!", true);
                        fetchAcordos();
                    })
                    .catch((err) => {
                        showSnackbar(err.message || "Erro ao estornar a baixa.", false);
                        setLoading(false);
                    });
            }
        );
    };

    const handleEmitirBoleto = (parcela) => {
        setLoading(true);
        getFile(`/acordo/parcela/boleto/${parcela.id}`, "boleto.pdf")
            .then(() => {
                showSnackbar("Boleto gerado (ou 2ª via) com sucesso!", true);
            })
            .catch(err => {
                showSnackbar(err.message || "Erro ao gerar boleto.", false);
            })
            .finally(() => setLoading(false));
    };

    const handleOpenEditParcela = (parcela) => {
        setSelectedParcelaEdit(parcela);
        setNovoVencimentoParcela(moment(parcela.vencimento).format("YYYY-MM-DD"));
        setEditParcelaDialogOpen(true);
    };

    const handleCloseEditParcela = () => {
        setEditParcelaDialogOpen(false);
        setSelectedParcelaEdit(null);
        setNovoVencimentoParcela("");
    };

    const handleConfirmEditParcela = () => {
        if (!selectedParcelaEdit || !novoVencimentoParcela) return;
        setLoading(true);
        postNoResponse("/acordo/parcela/update-vencimento", {
            idParcela: selectedParcelaEdit.id,
            novoVencimento: novoVencimentoParcela
        })
        .then(() => {
            showSnackbar("Vencimento da parcela atualizado com sucesso!", true);
            handleCloseEditParcela();
            fetchAcordos();
        })
        .catch(err => {
            showSnackbar(err.message || "Erro ao atualizar vencimento.", false);
            setLoading(false);
        });
    };

    const handleOpenUpdateBoleto = (parcela) => {
        setSelectedBoletoParcela(parcela);
        const dataAtrasada = moment(parcela.vencimento).isBefore(moment(), 'day') ? moment().format("YYYY-MM-DD") : moment(parcela.vencimento).format("YYYY-MM-DD");
        setNovaDataBoleto(dataAtrasada);
        setUpdateBoletoDialogOpen(true);
        simularAtualizacaoBoleto(parcela.id, dataAtrasada);
    };

    const handleCloseUpdateBoleto = () => {
        setUpdateBoletoDialogOpen(false);
        setSelectedBoletoParcela(null);
        setSimulacaoBoleto(null);
        setNovaDataBoleto("");
    };

    const simularAtualizacaoBoleto = (idParcela, novaData) => {
        if (!novaData) return;
        setSimulacaoLoading(true);
        get(`/acordo/parcela/boleto/simular/${idParcela}?novaData=${novaData}`)
            .then(res => setSimulacaoBoleto(res))
            .catch(err => showSnackbar(err.message || "Erro ao simular boleto.", false))
            .finally(() => setSimulacaoLoading(false));
    };

    const handleConfirmUpdateBoleto = () => {
        if (!selectedBoletoParcela || !novaDataBoleto || !simulacaoBoleto) return;
        setLoading(true);
        import('utils/WebService').then(({ postFile }) => {
            postFile("/acordo/parcela/boleto/reemitir", {
                idParcela: selectedBoletoParcela.id,
                novaData: novaDataBoleto,
                novoValor: simulacaoBoleto.valorFinal
            }, "boleto_reemitido.pdf")
            .then(() => {
                showSnackbar("Boleto reemitido e anterior cancelado!", true);
                handleCloseUpdateBoleto();
            })
            .catch(err => showSnackbar(err.message || "Erro ao reemitir boleto.", false))
            .finally(() => setLoading(false));
        });
    };

    const acordosFormalizados = acordos.filter(a => a.situacao !== 4);
    const acordosCancelados = acordos.filter(a => a.situacao === 4);

    const renderAcordos = (listaDeAcordos) => {
        return listaDeAcordos.map((acordo, idx) => (
            <Accordion key={acordo.id || idx} sx={{ mb: 1, boxShadow: "none", border: "1px solid", borderColor: "divider", bgcolor: "background.default", color: "text.main" }}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{ bgcolor: darkMode ? "rgba(0,0,0,0.2)" : "#fafbfc" }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <MDTypography variant="caption" color="text" display="block">Data</MDTypography>
                            <MDTypography variant="button" fontWeight="medium">{moment(acordo.dataFormalizacao).format("DD/MM/YYYY")}</MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <MDTypography variant="caption" color="text" display="block">Valor Total</MDTypography>
                            <MDTypography variant="button" fontWeight="medium">{toCurrency(acordo.valorAcordoTotal)}</MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <MDTypography variant="caption" color="text" display="block">Parcelas</MDTypography>
                            <MDTypography variant="button" fontWeight="medium">{acordo.qtdParcelas}x</MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <MDTypography variant="caption" color="text" display="block">Usuário</MDTypography>
                            <MDTypography variant="button" fontWeight="medium">{acordo.nomeUsuario}</MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <MDTypography variant="caption" color="text" display="block">Situação</MDTypography>
                            {getSituacaoLabel(acordo.situacao)}
                        </Grid>
                        <Grid item xs={12} sm={2} textAlign="right">
                            {acordo.situacao === 1 && (
                                <MDButton 
                                    variant="outlined" 
                                    color="error" 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenCancelDialog(acordo.id);
                                    }}
                                >
                                    <Icon sx={{ mr: 1 }}>cancel</Icon> Cancelar
                                </MDButton>
                            )}
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <MDTypography variant="caption" fontWeight="medium" display="block">Resumo Financeiro:</MDTypography>
                    </MDBox>

                    {acordo.situacao === 4 && (
                        <MDBox mb={2} p={1} bgcolor={darkMode ? "rgba(244, 67, 54, 0.2)" : "#ffeeee"} borderRadius={1}>
                            <MDTypography variant="caption" color="error" display="block">
                                <b>Cancelado por:</b> {acordo.nomeUsuarioCancelamento || 'Sistema'} em {acordo.dataCancelamento ? moment(acordo.dataCancelamento).format("DD/MM/YYYY HH:mm") : ''}
                            </MDTypography>
                        </MDBox>
                    )}
                    <MDBox mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={4} sm={2}>
                                <MDTypography variant="caption" color="text" display="block">Vlr. Original</MDTypography>
                                <MDTypography variant="caption">{toCurrency(acordo.valorOriginalTotal)}</MDTypography>
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <MDTypography variant="caption" color="text" display="block">Desconto</MDTypography>
                                <MDTypography variant="caption" color="success">-{toCurrency(acordo.descontoAplicado)}</MDTypography>
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <MDTypography variant="caption" color="text" display="block">Juros/Multa</MDTypography>
                                <MDTypography variant="caption" color="error">+{toCurrency((acordo.jurosAplicados || 0) + (acordo.multaAplicada || 0))}</MDTypography>
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <MDTypography variant="caption" color="text" display="block">Correção</MDTypography>
                                <MDTypography variant="caption" color="error">+{toCurrency(acordo.correcaoAplicada)}</MDTypography>
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <MDTypography variant="caption" color="text" display="block">Honorários</MDTypography>
                                <MDTypography variant="caption" color="error">+{toCurrency(acordo.honorariosAplicados)}</MDTypography>
                            </Grid>
                        </Grid>
                    </MDBox>
                    
                    <MDTypography variant="caption" fontWeight="medium" display="block" mb={1}>Cronograma de Pagamento:</MDTypography>
                    <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none", bgcolor: "background.default", borderColor: "divider" }}>
                        <Table size="small">
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Nº</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Tipo</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Vencimento</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Valor</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Situação</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Data Pgto</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Valor Pgto</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Desc.</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Acrésc.</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Ações</MDTypography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {acordo.parcelas && acordo.parcelas.map((p, pIdx) => (
                                    <TableRow key={pIdx}>
                                        <TableCell><MDTypography variant="caption">{p.numeroParcela}</MDTypography></TableCell>
                                        <TableCell>
                                            <MDTypography variant="caption" color={p.cbEntrada ? "success" : "text"}>
                                                {p.cbEntrada ? (p.cbMle ? "Entrada (MLE)" : "Entrada") : "Parcela"}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell><MDTypography variant="caption">{moment(p.vencimento).format("DD/MM/YYYY")}</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption">{toCurrency(p.valorParcela)}</MDTypography></TableCell>
                                        <TableCell>
                                            <MDTypography 
                                                variant="caption" 
                                                display="block" 
                                                color={p.situacao === 2 ? "success" : isParcelaAtrasada(p) ? "error" : (p.situacao === 1 && p.valorPago > 0 ? "warning" : "text")}
                                                fontWeight={isParcelaAtrasada(p) ? "bold" : "regular"}
                                            >
                                                {getParcelaSituacaoLabel(p)}
                                            </MDTypography>
                                            {getBoletoSituacaoBadge(p.situacaoBoleto, isParcelaAtrasada(p))}
                                        </TableCell>
                                        <TableCell><MDTypography variant="caption">{p.dataPagamento ? moment(p.dataPagamento).format("DD/MM/YYYY") : "-"}</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="success">{p.valorPago > 0 ? toCurrency(p.valorPago) : (p.situacao === 2 ? toCurrency(p.valorParcela) : "-")}</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="success">{p.valorDesconto > 0 ? toCurrency(p.valorDesconto) : "-"}</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="error">{p.valorAcrescimo > 0 ? toCurrency(p.valorAcrescimo) : "-"}</MDTypography></TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {!isViewOnly && p.situacao !== 2 && p.situacao !== 4 && (
                                                <MDButton variant="text" color="success" size="small" onClick={() => handleOpenBaixaDialog(p)}>
                                                    Baixar
                                                </MDButton>
                                            )}
                                            {!isViewOnly && p.situacao !== 2 && p.situacao !== 4 && !isMembro && (
                                                <MDButton variant="text" color="secondary" size="small" onClick={() => handleOpenEditParcela(p)} title="Editar Vencimento da Parcela">
                                                    Editar
                                                </MDButton>
                                            )}
                                            {p.situacao !== 2 && p.situacao !== 4 && acordo.emiteBoleto !== false && (
                                                <MDBox display="inline-flex" gap={1}>
                                                    <MDButton 
                                                        variant="text" 
                                                        color="info" 
                                                        size="small" 
                                                        onClick={() => handleEmitirBoleto(p)} 
                                                        title={p.nossoNumero ? "Baixar 2ª via do boleto atual" : "Emitir boleto original pela primeira vez"}
                                                    >
                                                        {p.nossoNumero ? "2ª Via" : "Emitir Boleto"}
                                                    </MDButton>
                                                    <MDButton 
                                                        variant="text" 
                                                        color="warning" 
                                                        size="small" 
                                                        onClick={() => handleOpenUpdateBoleto(p)} 
                                                        title="Cancelar o boleto atual e gerar um NOVO boleto com novo Nosso Número (mantendo o mesmo vencimento ou alterando)"
                                                    >
                                                        {p.nossoNumero ? "Reemitir (Novo Nosso N°)" : "Novo Vencimento"}
                                                    </MDButton>
                                                </MDBox>
                                            )}
                                            {!isViewOnly && p.situacao === 2 && (
                                                <MDButton variant="text" color="error" size="small" onClick={() => handleEstornar(p)}>
                                                    Estornar
                                                </MDButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        ));
    };

    return (
        <MDBox mt={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">Acordos Formalizados</MDTypography>
                {!isViewOnly && (
                    <MDButton variant="contained" color="info" size="small" onClick={() => changeTab && changeTab(2)}>
                        <Icon sx={{ mr: 1 }}>add</Icon> Formalizar Manual
                    </MDButton>
                )}
            </MDBox>

            {acordosFormalizados.length === 0 && !loading && (
                <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">Nenhum acordo encontrado para este devedor.</MDTypography>
                </MDBox>
            )}

            {renderAcordos(acordosFormalizados)}

            {acordosCancelados.length > 0 && (
                <MDBox mt={4}>
                    <MDBox mb={2}>
                        <MDTypography variant="h6">Acordos Cancelados</MDTypography>
                    </MDBox>
                    {renderAcordos(acordosCancelados)}
                </MDBox>
            )}
            
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />

            <Dialog 
                open={cancelDialogOpen} 
                onClose={handleCloseCancelDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>Cancelar Acordo</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja cancelar este acordo ativo? Esta ação não poderá ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseCancelDialog} color="secondary">
                        Não, Voltar
                    </MDButton>
                    <MDButton onClick={handleConfirmCancelAcordo} color="error">
                        Sim, Cancelar
                    </MDButton>
                </DialogActions>
            </Dialog>
            <Dialog 
                open={baixaDialogOpen} 
                onClose={handleCloseBaixaDialog} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>Baixa Manual de Parcela</DialogTitle>
                <DialogContent>
                    <MDBox mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    type="date"
                                    label="Data do Pagamento"
                                    value={baixaData}
                                    onChange={(e) => setBaixaData(e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={darkMode ? { "& input": { colorScheme: "dark" } } : {}}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    type="number"
                                    label="Valor Pago (R$)"
                                    value={baixaValor}
                                    onChange={(e) => setBaixaValor(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    type="number"
                                    label="Desconto (R$)"
                                    value={baixaDesconto}
                                    onChange={(e) => setBaixaDesconto(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    type="number"
                                    label="Acréscimo (R$)"
                                    value={baixaAcrescimo}
                                    onChange={(e) => setBaixaAcrescimo(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseBaixaDialog} color="secondary">Cancelar</MDButton>
                    <MDButton onClick={handleConfirmBaixa} color="success" disabled={loading}>Confirmar Baixa</MDButton>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={updateBoletoDialogOpen} 
                onClose={handleCloseUpdateBoleto} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>Reemitir Boleto (Novo Nosso Número)</DialogTitle>
                <DialogContent>
                    <DialogContentText mb={2}>
                        Confirme a data de vencimento para este boleto. Ao confirmar, o boleto anterior desta parcela será <strong>cancelado</strong> e um <strong>novo boleto com novo Nosso Número</strong> será gerado.
                    </DialogContentText>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <MDInput 
                                type="date" 
                                label="Nova Data de Vencimento" 
                                value={novaDataBoleto} 
                                onChange={(e) => {
                                    setNovaDataBoleto(e.target.value);
                                    if(selectedBoletoParcela) {
                                        simularAtualizacaoBoleto(selectedBoletoParcela.id, e.target.value);
                                    }
                                }} 
                                fullWidth 
                                InputLabelProps={{ shrink: true }}
                                sx={darkMode ? { "& input": { colorScheme: "dark" } } : {}}
                            />
                        </Grid>
                        {simulacaoBoleto && (
                            <>
                                <Grid item xs={12}>
                                    <MDTypography variant="caption" color="text">Simulação de Valores ({simulacaoBoleto.diasAtraso} dias de atraso):</MDTypography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDInput label="Valor Original" value={toCurrency(simulacaoBoleto.valorOriginal)} fullWidth disabled />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDInput label="Multa + Juros" value={toCurrency(simulacaoBoleto.multa + simulacaoBoleto.juros)} fullWidth disabled />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDInput label="Valor Final" value={toCurrency(simulacaoBoleto.valorFinal)} fullWidth disabled />
                                </Grid>
                            </>
                        )}
                        {simulacaoLoading && (
                            <Grid item xs={12}>
                                <MDTypography variant="caption" color="info">Calculando juros...</MDTypography>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseUpdateBoleto} color="secondary">Cancelar</MDButton>
                    <MDButton onClick={handleConfirmUpdateBoleto} color="success" disabled={loading || simulacaoLoading || !simulacaoBoleto}>Reemitir Novo Boleto</MDButton>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={editParcelaDialogOpen} 
                onClose={handleCloseEditParcela} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>Editar Vencimento da Parcela</DialogTitle>
                <DialogContent>
                    <MDBox mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    label="Vencimento Original"
                                    value={selectedParcelaEdit && selectedParcelaEdit.vencimentoOriginal ? moment(selectedParcelaEdit.vencimentoOriginal).format("DD/MM/YYYY") : (selectedParcelaEdit ? moment(selectedParcelaEdit.vencimento).format("DD/MM/YYYY") : "")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    type="date"
                                    label="Novo Vencimento"
                                    value={novoVencimentoParcela}
                                    onChange={(e) => setNovoVencimentoParcela(e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={darkMode ? { "& input": { colorScheme: "dark" } } : {}}
                                />
                            </Grid>
                        </Grid>
                        
                        {selectedParcelaEdit && selectedParcelaEdit.historicoVencimento && selectedParcelaEdit.historicoVencimento.length > 0 && (
                            <MDBox mt={3}>
                                <MDTypography variant="caption" fontWeight="medium" display="block" mb={1}>Histórico de Alterações:</MDTypography>
                                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none", bgcolor: "background.default", borderColor: "divider" }}>
                                    <Table size="small">
                                        <TableHead sx={{ display: "table-header-group" }}>
                                            <TableRow>
                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Data/Hora</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Anterior</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Novo</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Usuário</MDTypography></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedParcelaEdit.historicoVencimento.map((h, i) => (
                                                <TableRow key={i}>
                                                    <TableCell><MDTypography variant="caption">{moment(h.dataAlteracao).format("DD/MM/YYYY HH:mm")}</MDTypography></TableCell>
                                                    <TableCell><MDTypography variant="caption">{moment(h.vencimentoAnterior).format("DD/MM/YYYY")}</MDTypography></TableCell>
                                                    <TableCell><MDTypography variant="caption">{moment(h.vencimentoNovo).format("DD/MM/YYYY")}</MDTypography></TableCell>
                                                    <TableCell><MDTypography variant="caption">{h.nomeUsuarioAlteracao}</MDTypography></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </MDBox>
                        )}
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseEditParcela} color="secondary">Cancelar</MDButton>
                    <MDButton onClick={handleConfirmEditParcela} color="success" disabled={loading}>Salvar Alteração</MDButton>
                </DialogActions>
            </Dialog>

        </MDBox>
    );
}
